import React, { useEffect, useRef, useState } from "react";
import CustomButton from "src/components/CustomButton";
import { Checkbox, CircularProgress, FormControl, FormControlLabel, FormGroup, IconButton, InputLabel, MenuItem, Radio, RadioGroup, Select, TextareaAutosize } from "@mui/material";
import {
  Box,
  Divider,
  Grid,
  TextField,
  Tooltip,
  Typography,
  Button,
} from "@mui/material";
import backButton from "../../Images/backButton.svg";
import { SEND_BULK_EMAIL, SEND_BULK_WHATSAPP_MESSAGES } from "src/graphql/mutations";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import { User, sendMessagesPageProps } from "./sendBulkMessages";
import { uploadImage } from "src/components/Common/Utils";
import { useNavigate } from "react-router";
import { ROUTES } from "src/constant";

const SendBulkMessagesModal: React.FC<sendMessagesPageProps> = ({
  refetchSendMessages,
  handleClose,
  initialSendMessagesObj = {
    type: "",
    userType: "",
    businessType: "",
    emailBody: "",
    emailSubject: "",
    imageUrl: "",
    businessClaimType: "",
    personalEmailId: "",
    messageText: "",
    personalNumber: "",
    userList: [],
  },
  sendMessagesObj = {
    type: "",
    userType: "",
    businessType: "",
    emailBody: "",
    emailSubject: "",
    imageUrl: "",
    businessClaimType: "",
    personalEmailId: "",
    userList: [],
    messageText: "",
    personalNumber: "",
    _id: "",
  },
  setSendMessagesObj = () => { },
}) => {
  const [sendBulkEmail] = useMutation(SEND_BULK_EMAIL);
  const [sendBulkMessages] = useMutation(SEND_BULK_WHATSAPP_MESSAGES);
  const [error, setError] = useState(false);
  const imageRef = useRef<HTMLInputElement | null>(null);
  const [desktopUrl, setDesktopUrl] = useState<string>("");
  const [showEmailInput, setShowEmailInput] = useState(false);
  const [showNumberInput, setShowNumberInput] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const userList: User[] = sendMessagesObj?.userList || [];
  const [sendMode, setSendMode] = useState<"ALL" | "PERSONALLY">("ALL");
  const navigate = useNavigate();
  const [invalidNumbers, setInvalidNumbers] = useState<string[]>([]);
  const API_URL = process.env.REACT_APP_API_URL;
  const API_TOKEN = process.env.REACT_APP_API_TOKEN;


  useEffect(() => {
    const type = sendMessagesObj?.type;

    setShowEmailInput(type === "EMAIL");
    setShowNumberInput(type === "WHATSAPP");
  }, [sendMessagesObj?.type]);

  const handleFileChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    type: string
  ) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      const file = files?.[0];
      const fileUrl = URL.createObjectURL(file);

      setSendMessagesObj((prev: any) => ({
        ...prev,
        [type]: file,
      }));
    }
  };

  const handleNumberChange = (e: any) => {
    let value = e.target.value.replace(/\D/g, ""); // Remove all non-numeric characters
    if (value.length > 10) {
      value = value.slice(0, 10); // Trim to 10 digits if exceeded
    }
    setSendMessagesObj({ ...sendMessagesObj, personalNumber: value });
    setError(value.length !== 10); // Show error if not exactly 10 digits
  };


  const validateWhatsAppNumbers = async (numbers: string[]) => {
    const invalidList: string[] = [];

    await Promise.all(numbers.map(async (number) => {
      const chatId = `${number}@c.us`;
      try {
        const res = await fetch(`${API_URL}/contacts/check?token=${API_TOKEN}&chatId=${chatId}&nocache=true`);
        const data = await res.json();
        if (data.status !== "valid") {
          invalidList.push(number);
        }
      } catch (err) {
        console.error(`Validation failed for ${number}:`, err);
        invalidList.push(number); // assume invalid on error
      }
    }));

    setInvalidNumbers(invalidList);
    return invalidList;
  };



  const handleSubmit = async (e: any) => {
    e.preventDefault();

    if (!sendMessagesObj?.type) {
      toast.error("Please Select a Message Type.");
      return;
    }


    if (sendMessagesObj?.type === "EMAIL") {
      if (!sendMessagesObj?.emailSubject || sendMessagesObj?.emailSubject.trim() === "") {
        toast.error("Email subject is required for Email type.");
        return;
      }
      if (!sendMessagesObj?.emailBody || sendMessagesObj?.emailBody.trim() === "") {
        toast.error("Email body (message description) is required.");
        return;
      }
    }

    if (sendMessagesObj?.type === "WHATSAPP") {
      if (!sendMessagesObj?.messageText || sendMessagesObj?.messageText.trim() === "") {
        toast.error("WhatsApp message text is required.");
        return;
      }
    }

    try {
      setIsSending(true);
      if (
        sendMessagesObj?.imageUrl &&
        typeof sendMessagesObj?.imageUrl !== "string" &&
        sendMessagesObj?.imageUrl instanceof File
      ) {
        const response = await uploadImage(
          sendMessagesObj.imageUrl,
          "Messages",
          sendMessagesObj?.type ?? ""
        );

        if (response?.uploadImage?.url) {
          sendMessagesObj.imageUrl = response.uploadImage.url;
        }
      }

      const formattedUserList = userList.map(user => {
        let formattedNumber: any = user.mobileNumber || "";

        // Format the number if it exists and is for WhatsApp
        if (formattedNumber && sendMessagesObj?.type === "WHATSAPP") {
          if (formattedNumber.length === 10) {
            formattedNumber = `91${formattedNumber}`;
          } else if (formattedNumber.length === 11 && formattedNumber.startsWith('0')) {
            formattedNumber = `91${formattedNumber.substring(1)}`;
          }
        }
        return {
          email: user?.email || "",
          mobileNumber: formattedNumber,
          name: user?.name || user?.businessName,
          websiteLink: user?.websiteLink,
        };
      });

      const updatedSendBulkEmail: any = {
        emailBody: sendMessagesObj?.emailBody,
        emailSubject: sendMessagesObj?.emailSubject,
        imageUrl: sendMessagesObj?.imageUrl,
        personalEmailId: sendMessagesObj?.personalEmailId,
        userList: formattedUserList,
      };

      const updatedSendBulkMessages: any = {
        messageText: sendMessagesObj?.messageText,
        imageUrl: sendMessagesObj?.imageUrl,
        personalNumber: sendMessagesObj?.personalNumber,
        userList: formattedUserList,
      };
      if (sendMessagesObj?.type === "EMAIL") {
        const { data } = await sendBulkEmail({
          variables: { input: updatedSendBulkEmail },
        });
        if (data?.sendBulkEmail?.statusCode === 200) {
          toast.success(data?.sendBulkEmail?.message || "Messages Send successfully");
          setSendMessagesObj(initialSendMessagesObj);
          handleClose();
          navigate(ROUTES.SHOW_NOTIFICATIONS);
        } else {
          toast.error(data?.sendBulkEmail?.message || "Error Sending Messages");
        }
      } else if (sendMessagesObj?.type === "WHATSAPP") {

        const numbersToValidate = formattedUserList.map(u => u.mobileNumber);

        const invalid = await validateWhatsAppNumbers(numbersToValidate);

        if (invalid.length > 0) {
          toast.warning(`${invalid.length} invalid number(s) found. Continuing to send messages to valid numbers.`);
        }
        
        const { data } = await sendBulkMessages({
          variables: { input: updatedSendBulkMessages },
        });
        if (data?.sendBulkMessages?.statusCode === 200) {
          toast.success(data?.sendBulkMessages?.message || "Messages Send successfully");
          setSendMessagesObj(initialSendMessagesObj);
          handleClose();
          navigate(ROUTES.VIEW_MESSAGES);
        } else {
          toast.error(data?.sendBulkMessages?.message || "Error Sending Messages");
        }
      }
      // Refetch campaigns to update the list
      refetchSendMessages();

      // Provide feedback to the user

    } catch (error: any) {
      toast.error(error?.message || "An unexpected error occurred.");
      console.error(error || "An unexpected error occurred.");
    }
    finally {
      setIsSending(false);
    }
  };

  return (
    <Box p={2}>
      <Box style={{ display: "flex", alignItems: "center" }}>
        <Tooltip title="Back">
          <img
            src={backButton}
            alt="backButton"
            height={25}
            width={25}
            style={{ marginRight: "12px", cursor: "pointer" }}
            onClick={handleClose}
          />
        </Tooltip>
        <Typography
          variant="h4"
          gutterBottom
          style={{ color: "#00C5B9" }}
          marginTop={2}
        >
          Send Bulk Messages
        </Typography>
      </Box>
      <Box marginTop={2} width="100%">
        <FormControl component="fieldset" style={{ marginBottom: "16px" }}>
          <Typography variant="h6">Send Mode</Typography>
          <RadioGroup
            row
            value={sendMode}
            onChange={(e) => setSendMode(e.target.value as "ALL" | "PERSONALLY")}
          >
            <FormControlLabel value="ALL" control={<Radio />} label="All" />
            <FormControlLabel value="PERSONALLY" control={<Radio />} label="Personally" />
          </RadioGroup>
        </FormControl>
        {sendMode === "ALL" && (
          <>
            <Typography variant="h6">Selected Users ({userList.length})</Typography><Box
              style={{
                maxHeight: "200px",
                overflowY: "auto",
                border: "1px solid #ccc",
                padding: "8px",
                borderRadius: "4px",
              }}
            >
              {userList.length > 0 ? (
                userList.map((user: any, index: number) => (
                  <Box key={index} display="flex" alignItems="center" mb={1}>
                    <Typography variant="body2">
                      <strong>{index + 1}.</strong> {user?.name || user?.businessName} ({user?.userType}) - {user?.email || "N/A"} - {user?.mobileNumber || "N/A"}
                    </Typography>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No users selected
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
      <Box
        display="flex"
        justifyContent="space-between"
        mb={2}
        paddingRight={4}
      >
        <Grid item xs={6} paddingRight={4} width="100%" paddingTop="30px" display="flex" flexDirection="column" alignItems="flex-start">

          {/* radio button for select message type */}
          <FormControl>
            <Typography variant="h6">Select Message Type</Typography>
            <RadioGroup
              row
              value={sendMessagesObj?.type}
              onChange={(e) => setSendMessagesObj({
                ...sendMessagesObj,
                type: e.target.value,
              })}
            >
              <FormControlLabel value="EMAIL" control={<Radio />} label="Email" />
              <FormControlLabel value="WHATSAPP" control={<Radio />} label="WhatsApp" />
            </RadioGroup>
            {showEmailInput && sendMode === "PERSONALLY" && (
              <Box marginTop={2} width="100%">
                <TextField
                  name="personalEmailId"
                  margin="dense"
                  label="Enter Email Id For Testing"
                  type="text"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={sendMessagesObj?.personalEmailId || ""}
                  onChange={(e) =>
                    setSendMessagesObj({
                      ...sendMessagesObj,
                      personalEmailId: e.target.value,
                    })
                  }
                />
              </Box>
            )}
            {showNumberInput && sendMode === "PERSONALLY" && (
              <Box marginTop={2} width="100%">
                <TextField
                  name="personalNumber"
                  margin="dense"
                  label="Enter Mobile No. For Testing"
                  type="number"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={sendMessagesObj?.personalNumber || ""}
                  onChange={(e) =>
                    setSendMessagesObj({
                      ...sendMessagesObj,
                      personalNumber: e.target.value,
                    })
                  }
                />
              </Box>
            )}
          </FormControl>
          {/* input box for add emailSubject and message discriptions */}
          {showEmailInput && (
            <>
              <Box marginTop={2} width="100%">
                <TextField
                  name="emailSubject"
                  margin="dense"
                  label="Email Subject"
                  type="text"
                  InputLabelProps={{ shrink: true }}
                  fullWidth
                  value={sendMessagesObj?.emailSubject || ""}
                  onChange={(e) => setSendMessagesObj({ ...sendMessagesObj, emailSubject: e.target.value })}
                  disabled={sendMessagesObj?.type === "WHATSAPP"}
                />
              </Box>
              <Box marginTop={2} width="100%">
                <Typography variant="h6">Message Description</Typography>
                <TextareaAutosize
                  minRows={4}
                  style={{ width: "100%", padding: "8px", fontSize: "16px" }}
                  value={sendMessagesObj?.emailBody || ""}
                  onChange={(e) => setSendMessagesObj({ ...sendMessagesObj, emailBody: e.target.value })}
                />
              </Box>
            </>
          )}

          {showNumberInput && (
            <Box marginTop={2} width="100%">
              <Typography variant="h6">Message Description</Typography>
              <TextareaAutosize
                minRows={4}
                style={{ width: "100%", padding: "8px", fontSize: "16px" }}
                value={sendMessagesObj?.messageText || ""}
                onChange={(e) => setSendMessagesObj({ ...sendMessagesObj, messageText: e.target.value })}
              />
            </Box>
          )}

          <Box marginTop={2} width={"100%"}>
            <Typography variant="h6" gutterBottom>
              Image
            </Typography>
            {sendMessagesObj?.imageUrl ? (
              <Box
                position="relative"
                width="100%"
                height="auto"
                display={"flex"}
                justifyContent={"center"}
                alignItems={"center"}
              >
                <Box>
                  <img
                    src={
                      typeof sendMessagesObj?.imageUrl === "string"
                        ? sendMessagesObj?.imageUrl
                        : URL.createObjectURL(sendMessagesObj?.imageUrl)
                    }
                    alt="Message Image Upload"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "500px",
                      objectFit: "cover",
                    }}
                  />
                </Box>
                <CustomButton
                  variant="outlined"
                  color="warning"
                  style={{
                    position: "absolute",
                    top: "8px",
                    right: "8px",
                    zIndex: 1,
                    borderRadius: "50%",
                    width: "32px",
                    height: "32px",
                    minWidth: "0",
                    backgroundColor: "white",
                  }}
                  onClick={() => {
                    setSendMessagesObj((prev: any) => ({
                      ...prev,
                      imageUrl: "",
                    }));
                  }}
                  aria-label="Remove image"
                >
                  <Tooltip title="Remove">
                    <Box
                      sx={{
                        width: "100%",
                        height: "100%",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        borderRadius: "50%",
                      }}
                    >
                      &times;
                    </Box>
                  </Tooltip>
                </CustomButton>
              </Box>
            ) : (
              <label>
                <input
                  ref={imageRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={(e) => handleFileChange(e, "imageUrl")}
                />
                <CustomButton
                  variant="outlined"
                  size="small"
                  onClick={() => imageRef.current?.click()}
                >
                  Upload  Image
                </CustomButton>
                <TextField
                  label="Or Enter Image URL"
                  value={desktopUrl ?? (sendMessagesObj?.imageUrl || "")}
                  onBlurCapture={(e) => {
                    if (desktopUrl?.startsWith("https")) {
                      setSendMessagesObj((prev: any) => ({
                        ...prev,
                        imageUrl: desktopUrl,
                      }));
                    } else {
                      setDesktopUrl("");
                      setSendMessagesObj((prev: any) => ({
                        ...prev,
                        imageUrl: "",
                      }));
                    }
                  }}
                  onChange={(e) => setDesktopUrl(e.target.value)}
                  fullWidth
                  margin="normal"
                />
              </label>
            )}
          </Box>
          {invalidNumbers.length > 0 && (
            <Box mt={2}>
              <Typography variant="body2" color="error">
                Invalid Numbers:
              </Typography>
              <ul>
                {invalidNumbers.map((num, idx) => (
                  <li key={idx} style={{ color: "red" }}>{num}</li>
                ))}
              </ul>
            </Box>
          )}
          <Box marginTop={2} width={"100%"}>
            <CustomButton disabled={isSending} onClick={handleSubmit}>
              {isSending ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                "Send Messages"
              )}</CustomButton>
          </Box>
        </Grid>
      </Box>
    </Box>
  );
};

export default SendBulkMessagesModal;